import React from "react";
import AppContext from "@/js/common/AppContext";
import Axios from "@/js/common/Axios";
import TextBox from "@/js/common/ui/input/TextBox";
import Util from "@/js/common/Util";
import JSEncrypt from "jsencrypt";

interface Props {
    userId: string;
    setPswdModalOpen: Function;
    setMyInfoModalOpen: Function;
}

const ChangePswdDialog = ({ userId, setPswdModalOpen, setMyInfoModalOpen }: Props) => {
    const [nowPswd, setNowPswd] = React.useState<string>("");
    const [newPswd, setNewPswd] = React.useState<string>("");
    const [newPswdCfm, setNewPswdCfm] = React.useState<string>("");

    function doSavePswd() {
        if (!nowPswd) {
            AppContext.openAlert({
                title: "현재 비밀번호 확인",
                msg: "현재 비밀번호를 입력하세요.",
                icon: "warning"
            });
            return;
        }

        if (!newPswd) {
            AppContext.openAlert({
                title: "새 비밀번호 확인",
                msg: "새 비밀번호를 입력하세요.",
                icon: "warning"
            });
            return;
        }

        if (!Util.chkPasswdPtn(newPswd)) {
            AppContext.openAlert({
                title: "비밀번호 형식",
                msg: "비밀번호 입력형식이 맞지 않습니다.\n영문, 숫자, 특수문자를 포함한 8자 이상, 20자 이하로 입력하세요.",
                icon: "warning"
            });
            return;
        }

        if (!newPswdCfm) {
            AppContext.openAlert({
                title: "새 비밀번호 확인",
                msg: "새 비밀번호 확인을 입력하세요.",
                icon: "warning"
            });
            return;
        }

        if (newPswd !== newPswdCfm) {
            AppContext.openAlert({
                title: "비밀번호 확인",
                msg: "새 비밀번호와 비밀번호 확인이 일치하지 않습니다.",
                icon: "warning"
            });
            return;
        }

        if (nowPswd === newPswd) {
            AppContext.openAlert({
                title: "비밀번호 확인",
                msg: "현재 비밀번호와 새 비밀번호가 동일합니다. 다른 비밀번호를 입력하세요.",
                icon: "warning"
            });
            return;
        }

        AppContext.openAlert({
            title: "비밀번호 수정",
            msg: "비밀번호를 수정하시겠습니까?",
            icon: "question",
            confirmText: "수정",
            handleConfirm: () => {
                AppContext.showSpinner();

                Axios.dataAccess({
                    url: "portal/mbr/genRsaKeyTempUserId.do",
                    methodType: "post",
                    paramType: "object",
                    paramData: {
                        userId: userId
                    },
                    onSuccessFn: (res: any) => {
                        const pubKey = res.item.rsaInfo.pubKeyStr;
                        const encrypt = new JSEncrypt();
                        encrypt.setPublicKey(pubKey);

                        const encNowPswd = encrypt.encrypt(nowPswd);
                        const encNewPswd = encrypt.encrypt(newPswd);

                        if (!encNowPswd || !encNewPswd) {
                            AppContext.hideSpinner();
                            AppContext.openAlert({
                                title: "비밀번호 수정 실패",
                                msg: "비밀번호 암호화 처리에 실패하였습니다.",
                                icon: "warning"
                            });
                            return;
                        }

                        Axios.dataAccess({
                            url: "system/mbr/mng/save-my-pswd.do",
                            methodType: "post",
                            paramType: "json",
                            paramData: {
                                userId: userId,
                                encNowPswd: encNowPswd,
                                encNewPswd: encNewPswd
                            },
                            onSuccessFn: () => {
                                AppContext.hideSpinner();
                                setNowPswd("");
                                setNewPswd("");
                                setNewPswdCfm("");
                                setPswdModalOpen(false);
                                AppContext.openAlert({
                                    title: "비밀번호 수정 완료",
                                    msg: "비밀번호가 수정되었습니다.",
                                    icon: "check"
                                });
                            }
                        });
                    }
                });
            }
        });
    }

    return (
        <div className="w400 p10 bgcWhite">
            
            {/* 브라우저가 저장된 아이디, 비밀번호를 자동입력 할 수 없도록(거짓 텍스트박스) 조치 */}
            <div style={{ position: "absolute", opacity: 0, pointerEvents: "none", height: 0 }}>
                <input type="text" name="fake-username" autoComplete="username" />
                <input type="password" name="fake-password" autoComplete="current-password" />
            </div>

            <div className="dpFlx flxdCol mb15">
                <div className="mb5">
                    <span className="fm-b fs14 fc222222">현재 비밀번호</span>
                </div>
                <TextBox value={nowPswd}
                    width={400} height={36}
                    fontSize={14} fontFamily="fm-c" color="#333"
                    letterSpacing={-1} borderColor="#ddd"
                    borderOnColor="#222" borderRadius={2} type="password"
                    placeholder="현재 비밀번호를 입력하세요."
                    style={{ backgroundColor: "#fff", padding: "0 12px" }}
                    onChangeFunc={(data: string) => setNowPswd(data)}
                />
            </div>

            <div className="dpFlx flxdCol mb15">
                <div className="mb5">
                    <span className="fm-b fs14 fc222222">새 비밀번호</span>
                </div>
                <TextBox value={newPswd}
                    width={400} height={36}
                    fontSize={14} fontFamily="fm-c" color="#333"
                    letterSpacing={-1} borderColor="#ddd" 
                    borderOnColor="#222" borderRadius={2} type="password"
                    placeholder="새 비밀번호를 입력하세요."
                    style={{ backgroundColor: "#fff", padding: "0 12px" }}
                    onChangeFunc={(data: string) => setNewPswd(data)}
                />
            </div>

            <div className="dpFlx flxdCol">
                <div className="mb5">
                    <span className="fm-b fs14 fc222222">새 비밀번호 확인</span>
                </div>
                <TextBox value={newPswdCfm}
                    width={400} height={36}
                    fontSize={14} fontFamily="fm-c" color="#333"
                    letterSpacing={-1} borderColor="#ddd" borderOnColor="#222"
                    borderRadius={2} type="password"
                    placeholder="새 비밀번호를 다시 입력하세요."
                    style={{ backgroundColor: "#fff", padding: "0 12px" }}
                    onChangeFunc={(data: string) => setNewPswdCfm(data)}
                />
            </div>

            <div className="dpFlx jcC gap10 mt30">
                <div className="w100 h40 aiC jcC bdrd5 bdBox csP dpFlx" style={{ backgroundColor: "#bdc3c7" }}
                onClick={() => {
                    setPswdModalOpen(false);
                    setMyInfoModalOpen(true);
                }}>
                    <span className="fm-b fs15 fc222222">취소</span>
                </div>
                <div className="w120 h40 aiC jcC bdrd5 bdBox csP dpFlx" style={{ backgroundColor: "#007bff" }} onClick={doSavePswd}>
                    <span className="fm-b fs15 fcWhite">비밀번호 수정</span>
                </div>
            </div>
        </div>
    );
};

export default ChangePswdDialog;